import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, downloadFile } from '../api';
import { useToast, Modal, ConfirmModal, useIsAdmin } from '../components/shared';
import { LoginLogModal } from '../components/LoginLog';
import ClientForm, { TYPE_BADGE, STATUS, STATUS_BADGE, SPECIALTY, MapButton } from '../components/ClientForm';

const BASE = '/api';

function usePermCatalogue() {
  const [cat, setCat] = useState(null);
  useEffect(() => { fetch(`${BASE}/users/client-permissions`).then(r => r.json()).then(setCat).catch(() => {}); }, []);
  return cat;
}

function PermPicker({ cat, value, onChange }) {
  if (!cat) return <div className="text-dim text-sm">...</div>;
  const toggle = k => onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  return (
    <div dir="rtl" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 4 }}>
      {Object.entries(cat.options).map(([k, label]) => (
        <label key={k} className="flex items-center gap-8 text-sm" style={{ padding: '6px 8px', borderRadius: 6, cursor: 'pointer', background: value.includes(k) ? 'var(--primary-dim)' : 'transparent' }}>
          <input type="checkbox" checked={value.includes(k)} onChange={() => toggle(k)} />{label}
        </label>
      ))}
    </div>
  );
}

function UserFormModal({ client, user, onClose, onSaved }) {
  const cat = usePermCatalogue();
  const [form, setForm] = useState({ username: user ? user.username : '', password: '' });
  const [perms, setPerms] = useState(user ? user.permissions : null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  useEffect(() => { if (!perms && cat) setPerms(cat.defaults); }, [cat, perms]);

  async function submit() {
    if (!form.username || (!user && !form.password)) return toast('Username and password required', 'error');
    setLoading(true);
    try {
      const res = await fetch(user ? `${BASE}/users/${user.id}` : `${BASE}/users`, {
        method: user ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user
          ? { username: form.username, password: form.password || undefined, permissions: perms }
          : { ...form, role: 'client_viewer', client_id: client.id, permissions: perms }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast(user ? 'Login updated' : 'Login created'); onSaved(); onClose();
    } catch(e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }

  return (
    <Modal title={`${user ? 'Edit' : 'Add'} Login — ${client.name}`} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading || !perms}>{loading ? 'Saving...' : user ? 'Save' : 'Create'}</button>
      </>
    }>
      <p className="text-sm text-muted mb-16">This login only sees {client.name}'s data — and only the parts you tick below.</p>
      <div className="grid-2">
        <div className="field"><label>Username</label>
          <input className="input" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} autoFocus /></div>
        <div className="field"><label>{user ? 'New password (blank = keep)' : 'Password'}</label>
          <input className="input" type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
      </div>
      <div className="field"><label>يقدر يشوف إيه؟</label>
        <PermPicker cat={cat} value={perms || []} onChange={setPerms} /></div>
    </Modal>
  );
}

export function ClientUsers({ client, onClose }) {
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null); // 'new' | user
  const [logUser, setLogUser] = useState(null);
  const cat = usePermCatalogue();
  const toast = useToast();

  const loadUsers = () => {
    fetch(`${BASE}/users/by-client/${client.id}`).then(r => r.json()).then(setUsers).catch(() => {});
  };
  useEffect(() => { loadUsers(); }, []);

  async function deleteUser(id) {
    await fetch(`${BASE}/users/${id}`, { method: 'DELETE' });
    toast('User deleted'); loadUsers();
  }

  return (
    <Modal title={`Logins — ${client.name}`} onClose={onClose}>
      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setEdit('new')}>+ Add Login</button>
      </div>
      {users.length === 0 ? (
        <p className="text-dim text-sm">No logins yet.</p>
      ) : users.map(u => (
        <div key={u.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-12">
            <span className="mono fw-500">{u.username}</span>
            <span className="text-dim text-xs">{u.permissions.length} صلاحية</span>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setLogUser(u)}>📜 Log</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(u)}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}>Delete</button>
          </div>
          {cat && <div className="flex gap-8 mt-4" dir="rtl" style={{ flexWrap: 'wrap' }}>
            {u.permissions.map(k => <span key={k} className="badge badge-blue">{cat.options[k]}</span>)}
          </div>}
        </div>
      ))}
      {logUser && <LoginLogModal user={logUser} onClose={() => setLogUser(null)} />}
      {edit && <UserFormModal client={client} user={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={loadUsers} />}
    </Modal>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [facets, setFacets] = useState({});
  const [f, setF] = useState(() => { try { return JSON.parse(localStorage.getItem('pf_client_filters')) || {}; } catch (e) { return {}; } });
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [usersModal, setUsersModal] = useState(null);
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const nav = useNavigate();

  const load = useCallback(() => { api.getClients(f).then(setClients).catch(() => {}); }, [f]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.getClientFacets().then(setFacets).catch(() => {}); }, [clients.length]);
  useEffect(() => { try { localStorage.setItem('pf_client_filters', JSON.stringify(f)); } catch (e) {} }, [f]);
  const setFilter = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const active = Object.values(f).some(Boolean);

  async function handleSave(form) {
    if (modal?.id) { await api.updateClient(modal.id, form); toast('اتحفظ'); }
    else { await api.createClient(form); toast('العميل اتضاف'); }
    setModal(null); load();
  }

  async function handleDelete(id) {
    await api.deleteClient(id);
    toast('Client deleted'); setConfirm(null); load();
  }

  function exportExcel() {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
    downloadFile(`${BASE}/reports/clients?${q}`).catch(e => toast(e.message, 'error'));
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">CLIENTS <span>_</span></h1>
        <div className="flex gap-8">
          <button className="btn btn-ghost" onClick={exportExcel}>⬇ Excel</button>
          {isAdmin && <button className="btn btn-primary" onClick={() => setModal('new')}>+ New Client</button>}
        </div>
      </div>
      <div className="flex gap-8 mb-16" dir="rtl" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 260 }} placeholder="🔍 اسم / تليفون / كود" value={f.q || ''} onChange={setFilter('q')} />
        <select className="select" style={{ maxWidth: 170 }} value={f.governorate || ''} onChange={setFilter('governorate')}>
          <option value="">كل المحافظات</option>
          {(facets.governorate || []).map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="select" style={{ maxWidth: 140 }} value={f.status || ''} onChange={setFilter('status')}>
          <option value="">كل الحالات</option>
          {Object.entries(STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="select" style={{ maxWidth: 150 }} value={f.specialty || ''} onChange={setFilter('specialty')}>
          <option value="">كل التخصصات</option>
          {Object.entries(SPECIALTY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {active && <button className="btn btn-ghost btn-sm" onClick={() => setF({})}>✕ مسح الفلاتر</button>}
        <span className="text-dim text-sm">{clients.length} عميل</span>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Code</th><th>Name</th><th>Location</th><th>Status</th><th>Specialty</th><th>Contact</th><th>Devices</th><th></th></tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={8}><div className="empty">{active ? 'مفيش عملاء بالفلاتر دي' : 'No clients yet'}</div></td></tr>
            ) : clients.map(c => (
              <tr key={c.id} onClick={() => nav(`/clients/${c.id}`)} style={{ cursor: 'pointer' }}>
                <td className="mono text-xs text-dim">{c.client_code}</td>
                <td>
                  <div className="fw-600">{c.name}</div>
                  <div className="text-xs text-dim"><span className={`badge ${TYPE_BADGE[c.type] || 'badge-gray'}`} style={{ fontSize: 10 }}>{c.type}</span>
                    {c.parent_name && <span> · فرع من {c.parent_name}</span>}</div>
                </td>
                <td className="text-sm" dir="rtl">
                  {[c.district, c.area, c.governorate].filter(Boolean).join('، ') || <span className="text-dim">—</span>}
                </td>
                <td><span className={`badge ${STATUS_BADGE[c.status] || 'badge-gray'}`}>{STATUS[c.status] || c.status || '—'}</span></td>
                <td className="text-sm">{SPECIALTY[c.specialty] || <span className="text-dim">—</span>}</td>
                <td className="text-sm">
                  {c.contact_name && <div>{c.contact_name}{c.contact_role && <span className="text-dim"> · {c.contact_role}</span>}</div>}
                  <div className="text-muted" dir="ltr" style={{ textAlign: 'left' }}>{c.phone || '—'}</div>
                </td>
                <td className="mono text-sm">{c.device_count}{c.open_tickets > 0 && <span className="badge badge-yellow" style={{ marginLeft: 6 }}>{c.open_tickets} open</span>}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-8">
                    <MapButton client={c} small />
                    {isAdmin && <>
                      <button className="btn btn-ghost btn-sm" onClick={() => setUsersModal(c)}>🔑</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setModal(c)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirm(c.id)}>Del</button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && <ClientForm initial={modal === 'new' ? {} : modal} onSave={handleSave} onClose={() => setModal(null)} />}
      {confirm && <ConfirmModal message="Delete this client? Devices will be unlinked." onConfirm={() => handleDelete(confirm)} onClose={() => setConfirm(null)} />}
      {usersModal && <ClientUsers client={usersModal} onClose={() => setUsersModal(null)} />}
    </div>
  );
}
